/**
 * Image Utilities
 *
 * Two related concerns for Homepage slot imagery:
 *  1. Payload extraction — pull the raw base URL + alt text out of a slot
 *     payload, handling both DCA (imageSrc, imageUrls) and CF (assetPath) shapes.
 *  2. URL resolution — turn a raw/relative path into an absolute DAC / cache
 *     image URL, and normalize brand logo icons.
 *
 * (Responsive Dynamic Media rendition/srcset construction lives in
 * dynamicMediaUtils.ts, which depends on mi-web-ui-kit.)
 */
import type { renditions } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

function isRecord(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

// ─── Payload extraction (DCA / CF shapes) ───────────────────────────────────

const DCA_IMAGE_KEYS = ['image', 'logo', 'tertiaryImage'] as const;
export type DcaImageKey = (typeof DCA_IMAGE_KEYS)[number];

function isDcaImageNode(node: unknown): node is Record<string, unknown> {
  if (!isRecord(node)) return false;
  return (
    node['imageSrc'] != null ||
    (isRecord(node['imageUrls']) &&
      (node['imageUrls']['square'] != null || node['imageUrls']['standardHorizontal'] != null))
  );
}

function getBaseUrlFromDcaNode(node: Record<string, unknown>): string {
  const imageUrls = isRecord(node['imageUrls']) ? node['imageUrls'] : null;
  return (
    (typeof node['imageSrc'] === 'string' ? node['imageSrc'] : '') ||
    (typeof imageUrls?.['square'] === 'string' ? imageUrls['square'] : '') ||
    (typeof imageUrls?.['standardHorizontal'] === 'string' ? imageUrls['standardHorizontal'] : '') ||
    ''
  );
}

function getAltFromDcaNode(node: Record<string, unknown>): string {
  return (typeof node['alternateDescription'] === 'string' ? node['alternateDescription'] : '') || '';
}

/**
 * Extracts base URL for image from payload.
 * DCA: imageSrc OR imageUrls.square OR imageUrls.standardHorizontal (in that order)
 * CF: assetPath
 * When fromKey is provided, extract from that DCA node (logo/image/tertiaryImage) first; fall back to CF.
 */
export function getBaseUrlFromPayload(payload: Record<string, unknown>, fromKey?: DcaImageKey): string {
  if (fromKey) {
    const node = payload[fromKey];
    if (isDcaImageNode(node)) return getBaseUrlFromDcaNode(node);
    return (typeof payload['assetPath'] === 'string' ? payload['assetPath'] : '') || '';
  }
  const imageObj = isRecord(payload['image']) ? payload['image'] : null;
  const imageUrls = imageObj && isRecord(imageObj['imageUrls']) ? imageObj['imageUrls'] : null;
  const dcaImageUrl =
    (typeof imageObj?.['imageSrc'] === 'string' ? imageObj['imageSrc'] : '') ||
    (typeof imageUrls?.['square'] === 'string' ? imageUrls['square'] : '') ||
    (typeof imageUrls?.['standardHorizontal'] === 'string' ? imageUrls['standardHorizontal'] : '') ||
    '';
  return (typeof payload['assetPath'] === 'string' ? payload['assetPath'] : '') || dcaImageUrl || '';
}

/**
 * Extracts alt text for image from payload.
 * CF: altText OR imageAltText (payload top-level)
 * DCA: imageAltText (payload top-level) OR image.alternateDescription
 * When fromKey is provided, extract from that DCA node first; fall back to CF.
 */
export function getAltFromPayload(payload: Record<string, unknown>, fromKey?: DcaImageKey): string {
  if (fromKey) {
    const node = payload[fromKey];
    if (isDcaImageNode(node)) return getAltFromDcaNode(node);
    return (
      (typeof payload['imageAltText'] === 'string' ? payload['imageAltText'] : '') ||
      (typeof payload['altText'] === 'string' ? payload['altText'] : '') ||
      ''
    );
  }
  const imageObj = isRecord(payload['image']) ? payload['image'] : null;
  return (
    (typeof payload['imageAltText'] === 'string' ? payload['imageAltText'] : '') ||
    (typeof payload['altText'] === 'string' ? payload['altText'] : '') ||
    (typeof imageObj?.['alternateDescription'] === 'string' ? imageObj['alternateDescription'] : '') ||
    ''
  );
}

/**
 * Extracts baseUrl and alt from payload in one call.
 * When fromKey is provided (e.g. 'logo'), extract from that DCA node first; fall back to CF.
 */
export function getBaseUrlAndAltFromPayload(
  payload: Record<string, unknown>,
  fromKey?: DcaImageKey
): { baseUrl: string; alt: string } {
  return {
    baseUrl: getBaseUrlFromPayload(payload, fromKey),
    alt: getAltFromPayload(payload, fromKey),
  };
}

// ─── DAC / cache URL resolution ─────────────────────────────────────────────

export const EMPTY_RENDITIONS: renditions = { altText: '', assetPath: '', dynamic: false, renditions: [] };
export const DEFAULT_DAC_IMAGE_DOMAIN = 'https://cache.marriott.com';

export function isAbsoluteUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
}

export function getDacImageDomain(): string {
  const envVars = useClientEnvVarsStore.getState?.().envVarsObject as Record<string, unknown> | undefined;
  const homepageImageDomain =
    typeof envVars?.['UXL_IMAGE_CACHE_DOMAIN'] === 'string' ? envVars['UXL_IMAGE_CACHE_DOMAIN'].trim() : '';
  const compatibilityImageDomain =
    typeof envVars?.['DAC_IMG_DOMAIN'] === 'string' ? envVars['DAC_IMG_DOMAIN'].trim() : '';
  return homepageImageDomain || compatibilityImageDomain || DEFAULT_DAC_IMAGE_DOMAIN;
}

export function resolveImageUrl(url: string): string {
  const normalizedUrl = url?.trim();
  if (!normalizedUrl || isAbsoluteUrl(normalizedUrl)) return normalizedUrl;
  const imageDomain = getDacImageDomain().replace(/\/+$/, '');
  return `${imageDomain}${normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`}`;
}

export function resolveBrandLogoIcon(c: Record<string, unknown>): string {
  const rawBrandLogoIcon = c['brandLogoIcon'];
  if (typeof rawBrandLogoIcon === 'string' && rawBrandLogoIcon) return rawBrandLogoIcon;
  if (Array.isArray(rawBrandLogoIcon) && typeof rawBrandLogoIcon[0] === 'string' && rawBrandLogoIcon[0]) {
    return rawBrandLogoIcon[0];
  }

  const logoObj = isRecord(c['logo']) ? c['logo'] : null;
  const logoImageSrc = logoObj && typeof logoObj['imageSrc'] === 'string' ? logoObj['imageSrc'] : '';
  if (!logoImageSrc) return '';
  // Added the following check if the logo image src is already a brand logo icon
  if (logoImageSrc.startsWith('mcom-common:brands/')) return logoImageSrc;

  const brandCodeMatch = logoImageSrc.match(/\/brand-logos\/([^/.]+)(?:\.[a-zA-Z0-9]+)?$/i);
  if (brandCodeMatch?.[1]) return `mcom-common:brands/${brandCodeMatch[1]}`;

  return resolveImageUrl(logoImageSrc);
}
